import client from '@/services/http/client'
import type {
  AfterSaleGroupDetail,
  AfterSaleGroupDetailV2,
  AfterSaleGroupPage,
  AfterSaleGroupSummary,
  AfterSaleHistoryPageV2,
  AfterSaleArchiveResult,
  AfterSaleEvidenceMessage,
  AfterSaleIssue,
  AfterSaleIssueCreatePayload,
  AfterSaleIssueUpdatePayload,
  AfterSaleIssueUpdateResult,
  AfterSaleReviewCandidate,
  AfterSaleReviewDecisionPayload,
  AfterSaleReviewDecisionResult,
  AfterSaleReviewDetail,
  AfterSaleSyncRunStartResult,
} from '@/types/afterSales'

export async function fetchAfterSalesGroups(params: {
  search?: string
  sort?: 'updated_desc' | 'issues_desc' | 'name_asc'
  serviceOwner?: string
  salesOwner?: string
  page: number
  pageSize: number
}): Promise<AfterSaleGroupPage> {
  const { data } = await client.get<AfterSaleGroupPage>('/after-sales/groups', { params })
  return data
}

export async function exportFilteredAfterSalesIssues(params: {
  search?: string
  sort?: 'updated_desc' | 'issues_desc' | 'name_asc'
  serviceOwner?: string
  salesOwner?: string
  status?: 'ALL' | AfterSaleIssue['status']
  issueSearch?: string
}): Promise<number> {
  const response = await client.get<Blob>('/after-sales/issues/export', {
    params,
    responseType: 'blob',
    timeout: 120_000,
  })
  downloadBlob(
    response.data,
    `售后问题-筛选结果-${new Date().toISOString().slice(0, 10)}.csv`,
  )
  return Number(response.headers['x-exported-issue-count'] || 0)
}

export function exportAfterSalesIssues(
  group: AfterSaleGroupSummary,
  issues: AfterSaleIssue[],
): void {
  const headers = [
    '群名称',
    '客户',
    '接待负责人',
    '销售负责人',
    '问题ID',
    '问题标题',
    '问题描述',
    '状态',
    '处理阶段',
    '解决方案',
    '下一步动作',
    '首次发现',
    '最近发现',
    '更新时间',
    '更新来源',
    '问题解决负责人',
    '问题来源',
  ]
  const rows = issues.map((issue) => [
    group.groupName,
    group.customerName,
    group.serviceOwnerName || group.serviceOwner || '',
    group.salesOwner || '',
    issue.id,
    issue.title,
    issue.description,
    issueStatusLabel(issue.status),
    processingStageLabel(issue.processingStage),
    issue.solution,
    issue.nextAction || '',
    issue.firstSeenAt || '',
    issue.lastSeenAt || '',
    issue.updatedAt || '',
    issue.updatedBy || '',
    issue.issueOwner || '',
    issue.issueOrigin || '',
  ])
  const csv = [headers, ...rows]
    .map((row) => row.map(formatCsvCell).join(','))
    .join('\r\n')
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' })
  downloadBlob(blob, `售后问题-${safeFilename(group.groupName)}-${new Date().toISOString().slice(0, 10)}.csv`)
}

function issueStatusLabel(status: AfterSaleIssue['status']) {
  return {
    PENDING: '待处理',
    IN_PROGRESS: '处理中',
    RESOLVED: '已解决',
    ARCHIVED: '已归档',
  }[status]
}

function processingStageLabel(stage: AfterSaleIssue['processingStage']) {
  if (!stage) return ''
  return {
    PROCESSING: '处理中',
    BLOCKED: '受阻',
    VERIFYING: '验证中',
    REVIEW_PENDING: '待复核',
  }[stage]
}

function formatCsvCell(value: unknown) {
  const raw = String(value ?? '')
  const text = ['=', '+', '-', '@'].includes(raw.slice(0, 1)) ? `'${raw}` : raw
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function safeFilename(value: string) {
  return value.replace(/[\\/:*?"<>|]/g, '_').trim() || '未命名群'
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export async function fetchAfterSalesGroup(
  roomId: string,
  summary?: AfterSaleGroupSummary,
): Promise<AfterSaleGroupDetail> {
  if (summary?.authorityBackend === 'AFTER_SALE_V2') {
    return fetchAfterSalesGroupV2(roomId, summary)
  }

  const { data } = await client.get<AfterSaleGroupDetail>(
    `/after-sales/groups/${encodeURIComponent(roomId)}`,
  )
  return { ...data, apiMode: 'legacy', reviews: [], evidenceByMsgId: {} }
}

async function fetchAfterSalesGroupV2(
  roomId: string,
  summary?: AfterSaleGroupSummary,
): Promise<AfterSaleGroupDetail> {
  const encodedRoomId = encodeURIComponent(roomId)
  const [{ data: detail }, historyResult, reviewsResult] = await Promise.all([
    client.get<AfterSaleGroupDetailV2>(`/after-sales/v2/groups/${encodedRoomId}`),
    fetchLatestV2History(encodedRoomId).catch(() => null),
    fetchAfterSalesReviewsV2(encodedRoomId),
  ])
  const issues = (detail.issues || []).map(normalizeV2Issue)
  const evidenceByMsgId = await fetchAfterSalesEvidenceForIssuesV2(encodedRoomId, issues)
    .catch(() => ({}))
  return {
    roomId: detail.roomId,
    groupName: summary?.groupName || detail.roomId,
    customerName: summary?.customerName || '',
    issueCount: summary?.issueCount ?? issues.length,
    openIssueCount: summary?.openIssueCount
      ?? issues.filter((issue) => issue.status !== 'RESOLVED' && issue.status !== 'ARCHIVED').length,
    updatedAt: summary?.updatedAt || detail.committedAt || null,
    authorityBackend: summary?.authorityBackend || 'AFTER_SALE_V2',
    version: detail.stateVersion,
    apiMode: 'v2',
    stateVersion: detail.stateVersion,
    stateHash: detail.stateHash,
    committedAt: detail.committedAt,
    syncStatus: detail.syncStatus,
    issues,
    changes: flattenV2History(historyResult),
    reviews: reviewsResult.filter((review) => review.status === 'PENDING'),
    evidenceByMsgId,
  }
}

async function fetchAfterSalesReviewsV2(encodedRoomId: string) {
  const { data } = await client.get<AfterSaleReviewCandidate[]>(
    `/after-sales/v2/groups/${encodedRoomId}/reviews`,
  )
  return Array.isArray(data) ? data.map(normalizeReviewCandidate) : []
}

export async function fetchAfterSalesReviewDetail(
  roomId: string,
  reviewId: string,
): Promise<AfterSaleReviewDetail> {
  const { data } = await client.get<AfterSaleReviewDetail>(
    `/after-sales/v2/groups/${encodeURIComponent(roomId)}/reviews/${encodeURIComponent(reviewId)}`,
  )
  return data
}

export async function decideAfterSalesReview(
  roomId: string,
  reviewId: string,
  payload: AfterSaleReviewDecisionPayload,
): Promise<AfterSaleReviewDecisionResult> {
  const { data } = await client.post<AfterSaleReviewDecisionResult>(
    `/after-sales/v2/groups/${encodeURIComponent(roomId)}/reviews/${encodeURIComponent(reviewId)}/decision`,
    payload,
    { headers: { 'Idempotency-Key': createIdempotencyKey() } },
  )
  return data
}

function normalizeReviewCandidate(review: AfterSaleReviewCandidate): AfterSaleReviewCandidate {
  const referencedItemKeys = [
    ...(review.itemKeys || []),
    ...((review.reasonCode || '').match(/(?:ITEM|issue)-[A-Za-z0-9-]+/g) || []),
  ]
  return {
    ...review,
    relatedItemKeys: Array.from(new Set(referencedItemKeys)),
  }
}

async function fetchAfterSalesEvidenceForIssuesV2(
  encodedRoomId: string,
  issues: AfterSaleIssue[],
) {
  const msgIds = Array.from(new Set(
    issues.flatMap((issue) =>
      (issue.observations || []).flatMap((observation) => observation.evidenceMsgIds || []),
    ),
  )).filter(Boolean).slice(0, 100)
  if (!msgIds.length) return {}

  const params = new URLSearchParams()
  msgIds.forEach((msgId) => params.append('msgId', msgId))
  const { data } = await client.get<AfterSaleEvidenceMessage[]>(
    `/after-sales/v2/groups/${encodedRoomId}/evidence`,
    { params },
  )
  return Object.fromEntries((data || []).map((item) => [item.msgId, item]))
}

async function fetchLatestV2History(encodedRoomId: string, limit = 20) {
  const firstPage = await fetchV2HistoryPage(encodedRoomId, 0, limit)
  if (firstPage.total <= limit) {
    return firstPage
  }
  return fetchV2HistoryPage(encodedRoomId, Math.max(firstPage.total - limit, 0), limit)
}

async function fetchV2HistoryPage(encodedRoomId: string, offset: number, limit: number) {
  const { data } = await client.get<AfterSaleHistoryPageV2>(
    `/after-sales/v2/groups/${encodedRoomId}/history`,
    { params: { offset, limit } },
  )
  return data
}

function normalizeV2Issue(issue: AfterSaleIssue): AfterSaleIssue {
  return {
    ...issue,
    id: issue.itemKey || issue.id,
    updatedAt: issue.statusChangedAt || issue.lastSeenAt || issue.updatedAt,
    updatedBy: issue.statusOrigin || issue.updatedBy,
  }
}

function flattenV2History(history: AfterSaleHistoryPageV2 | null) {
  return (history?.versions || []).flatMap((version) =>
    (version.events || []).map((event) => ({
      ...event,
      issueId: event.issueId || event.itemKey,
      operatedAt: event.operatedAt || event.recordedAt || version.recordedAt || '',
    })),
  )
}

export function buildAfterSalesV2Patch(
  original: AfterSaleIssue,
  edited: AfterSaleIssueUpdatePayload,
) {
  const patch: Record<string, unknown> = {}
  const comparableFields = ['title', 'description', 'solution', 'sendEmail', 'status'] as const
  for (const field of comparableFields) {
    if (edited[field] !== original[field]) {
      patch[field] = edited[field]
    }
  }
  if (edited.processingStage !== original.processingStage) {
    patch.processingStage = edited.processingStage ?? null
  }
  if (edited.nextAction !== original.nextAction) {
    patch.nextAction = edited.nextAction || null
  }
  if ((edited.issueOwner || '') !== (original.issueOwner || '')) {
    patch.issueOwner = edited.issueOwner || ''
  }
  if (patch.status && patch.status !== 'IN_PROGRESS') {
    patch.processingStage = null
  }
  if (patch.status === 'RESOLVED' || patch.status === 'ARCHIVED') {
    patch.nextAction = null
  }
  return patch
}

export async function updateAfterSalesIssue(
  roomId: string,
  issueId: string,
  payload: AfterSaleIssueUpdatePayload,
): Promise<AfterSaleIssueUpdateResult> {
  if (payload.apiMode === 'v2') {
    return patchAfterSalesIssueV2(roomId, issueId, payload)
  }

  const { apiMode, originalIssue, expectedItemVersion, processingStage, nextAction, reason, ...legacyPayload } = payload
  void apiMode
  void originalIssue
  void expectedItemVersion
  void processingStage
  void nextAction
  void reason

  const { data } = await client.put<AfterSaleIssueUpdateResult>(
    `/after-sales/groups/${encodeURIComponent(roomId)}/issues/${encodeURIComponent(issueId)}`,
    legacyPayload,
  )
  return data
}

async function patchAfterSalesIssueV2(
  roomId: string,
  issueId: string,
  payload: AfterSaleIssueUpdatePayload,
): Promise<AfterSaleIssueUpdateResult> {
  if (!payload.originalIssue) {
    throw new Error('缺少原始问题版本，无法保存 V2 修改')
  }
  const patch = buildAfterSalesV2Patch(payload.originalIssue, payload)
  if (!Object.keys(patch).length) {
    throw new Error('没有需要保存的修改')
  }

  const { data } = await client.patch<AfterSaleIssueUpdateResult>(
    `/after-sales/v2/groups/${encodeURIComponent(roomId)}/issues/${encodeURIComponent(issueId)}`,
    {
      expectedItemVersion: payload.expectedItemVersion,
      patch,
      reason: payload.reason?.trim() || '',
    },
    {
      headers: { 'Idempotency-Key': createIdempotencyKey() },
    },
  )
  return {
    ...data,
    version: data.stateVersion ?? data.version,
    issue: normalizeV2Issue(data.issue),
  }
}

export async function startAfterSalesGroupSyncV2(roomId: string): Promise<AfterSaleSyncRunStartResult> {
  const { data } = await client.post<AfterSaleSyncRunStartResult>(
    `/after-sales/v2/groups/${encodeURIComponent(roomId)}/sync`,
  )
  return data
}

export async function createAfterSalesIssueV2(
  roomId: string,
  payload: AfterSaleIssueCreatePayload,
): Promise<AfterSaleIssueUpdateResult> {
  const { data } = await client.post<AfterSaleIssueUpdateResult>(
    `/after-sales/v2/groups/${encodeURIComponent(roomId)}/issues`,
    payload,
    { headers: { 'Idempotency-Key': createIdempotencyKey() } },
  )
  return { ...data, version: data.stateVersion ?? data.version, issue: normalizeV2Issue(data.issue) }
}

export async function createAfterSalesIssueLegacy(
  roomId: string,
  payload: AfterSaleIssueCreatePayload,
): Promise<AfterSaleIssueUpdateResult> {
  const { data } = await client.post<AfterSaleIssueUpdateResult>(
    `/after-sales/groups/${encodeURIComponent(roomId)}/issues`,
    {
      title: payload.title,
      description: payload.description || '',
      solution: payload.solution || '',
      sendEmail: payload.sendEmail,
    },
  )
  return data
}

export async function archiveAfterSalesGroupV2(roomId: string): Promise<AfterSaleArchiveResult> {
  const { data } = await client.post<AfterSaleArchiveResult>(
    `/after-sales/v2/groups/${encodeURIComponent(roomId)}/archive`,
  )
  return data
}

export async function exportAfterSalesGroupV2(roomId: string, groupName: string): Promise<void> {
  const response = await client.get<Blob>(
    `/after-sales/v2/groups/${encodeURIComponent(roomId)}/export`,
    { responseType: 'blob', timeout: 120_000 },
  )
  downloadBlob(response.data, `售后问题-${safeFilename(groupName)}-${new Date().toISOString().slice(0, 10)}.xlsx`)
}

function createIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16)
    return (char === 'x' ? value : (value & 0x3) | 0x8).toString(16)
  })
}
